import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase, refreshSupabaseConnection, setActiveTeamId } from '../lib/supabase';

const AuthContext = createContext({});
// Shopify session recovery can take longer than a normal app load because
// Supabase may still be exchanging and persisting the callback session.
const AUTH_SESSION_TIMEOUT_MS = 45000;
const AUTH_WORKSPACE_LOAD_TIMEOUT_MS = 90000;
const AUTH_CALLBACK_TIMEOUT_MS = 90000;
const AUTH_SESSION_TIMEOUT_MESSAGE = 'Session check timed out. Please refresh and try again.';
const AUTH_WORKSPACE_TIMEOUT_MESSAGE = 'Loading your workspace timed out. Please refresh and try again.';
const SHOPIFY_CALLBACK_TIMEOUT_MESSAGE = 'Shopify sign-in timed out while restoring your session. Please reconnect your store.';
const PUBLIC_ROUTE_PATHS = new Set([
  '/',
  '/login',
  '/onboarding',
  '/reset-password',
  '/change-password',
  '/privacy',
  '/terms',
  '/shopify-install',
  '/pricing',
]);

const withTimeout = async (promise, timeoutMs, timeoutMessage) => {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const isTimeoutError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('timed out');
};

const shouldSuppressWorkspaceTimeoutOnPublicRoute = (error) => {
  if (!isTimeoutError(error) || typeof window === 'undefined') {
    return false;
  }

  return PUBLIC_ROUTE_PATHS.has(window.location.pathname || '/');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [team, setTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const loadInFlightRef = useRef(false);
  const lastLoadedUserIdRef = useRef(null);
  const lastLoadedAtRef = useRef(0);
  const lastAuthErrorRef = useRef('');

  const setConnectionErrorOnce = useCallback((message) => {
    const normalized = String(message || '').trim();
    if (!normalized || lastAuthErrorRef.current === normalized) {
      return;
    }

    lastAuthErrorRef.current = normalized;
    setConnectionError(normalized);
  }, []);

  const clearAuthTokenHash = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hash = window.location.hash || '';
    if (!hash.includes('access_token') && !hash.includes('refresh_token')) {
      return;
    }

    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, []);

  // Refresh database connection
  const refreshConnection = async () => {
    try {
      setConnectionError(null);
      const result = await refreshSupabaseConnection();
      
      if (result?.success) {
        // Re-initialize auth state after refresh
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.user) {
          setUser(session?.user);
          const profile = await fetchUserProfile(session?.user?.id);
          setUserProfile(profile || null);
          const { allTeams, activeTeam } = await fetchTeams(session?.user?.id);
          setTeams(allTeams);
          setTeam(activeTeam);
        }
      } else {
        setConnectionErrorOnce(result?.error);
      }
      
      return result;
    } catch (error) {
      const errorMsg = error?.message || 'Connection refresh failed';
      setConnectionErrorOnce(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Fetch user profile from database
  const fetchUserProfile = async (userId) => {
    try {
      setProfileLoading(true);
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const getShopifyReturnContext = () => {
    if (typeof window === 'undefined') {
      return { isShopifyReturn: false, shopDomain: '', teamIdFromUrl: '' };
    }

    const params = new URLSearchParams(window.location.search);
    const shopDomain = (params.get('shop') ?? '').trim().toLowerCase();
    const teamIdFromUrl = (params.get('team_id') ?? '').trim();
    const shopifyStatus = (params.get('shopify') ?? '').trim().toLowerCase();

    return {
      isShopifyReturn: shopifyStatus === 'connected' || Boolean(shopDomain),
      shopDomain,
      teamIdFromUrl,
    };
  };

  const resolveShopifyTeamOverride = async (allTeams, shopDomain, teamIdFromUrl) => {
    if (teamIdFromUrl) {
      return allTeams.find((team) => team.id === teamIdFromUrl) || null;
    }

    if (!shopDomain) {
      return null;
    }

    try {
      const { data, error } = await supabase
        ?.from('integrations')
        ?.select('team_id')
        ?.eq('provider', 'shopify')
        ?.eq('shop_domain', shopDomain)
        ?.eq('is_active', true)
        ?.order('updated_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      if (error) {
        throw error;
      }

      return data?.team_id
        ? allTeams.find((team) => team.id === data.team_id) || null
        : null;
    } catch (error) {
      console.error('Error resolving Shopify team override:', error);
      return null;
    }
  };

  // Fetch all team memberships from database
  const fetchTeams = async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('team_members')
        ?.select('team_id, role, status, teams(id, name)')
        ?.eq('user_id', userId)
        ?.eq('status', 'active');

      if (error || !data || data.length === 0) return { allTeams: [], activeTeam: null };

      const allTeams = data.map((m) => ({
        id: m?.team_id,
        name: m?.teams?.name || null,
        role: m?.role,
      }));

      const { isShopifyReturn, shopDomain, teamIdFromUrl } = getShopifyReturnContext();
      const shopifyTeam = isShopifyReturn
        ? await resolveShopifyTeamOverride(allTeams, shopDomain, teamIdFromUrl)
        : null;

      // Restore last-selected team from localStorage, or use first
      const savedTeamId = localStorage.getItem(`makersstuff_active_team_${userId}`);
      const savedTeam = savedTeamId ? allTeams.find((t) => t.id === savedTeamId) : null;
      const activeTeam = isShopifyReturn
        ? (shopifyTeam || allTeams[0])
        : (savedTeam || allTeams[0]);

      // Set the header so RLS scopes to this team
      setActiveTeamId(activeTeam.id);

      return { allTeams, activeTeam };
    } catch (error) {
      console.error('Error fetching teams:', error);
      return { allTeams: [], activeTeam: null };
    }
  };

  // Switch to a different team — updates header, state, and localStorage
  const switchTeam = useCallback((teamId) => {
    const target = teams.find((t) => t.id === teamId);
    if (!target) return;

    setActiveTeamId(teamId);
    setTeam(target);
    if (user?.id) {
      localStorage.setItem(`makersstuff_active_team_${user.id}`, teamId);
    }
  }, [teams, user]);

  // Re-fetch teams from DB and update state.
  // Called by onboarding after create_team_with_owner RPC so AuthContext
  // learns about the new team without requiring a page reload.
  // Without this, teamId stays null → ProtectedRoute redirects back to
  // onboarding → infinite redirect loop burning DB requests.
  const refreshTeams = useCallback(async () => {
    if (!user?.id) return;
    const { allTeams, activeTeam } = await fetchTeams(user.id);
    setTeams(allTeams);
    setTeam(activeTeam);
  }, [user]);

  // Initialize auth session
  useEffect(() => {
    let mounted = true;
    let initHandled = false; // prevent double-fetch between initAuth and onAuthStateChange
    let authCallbackTimeoutId = null;

    // Check if there are auth tokens in the URL hash (magic link, OAuth callback)
    const hash = window.location.hash;
    const hasAuthTokensInHash = hash && (hash.includes('access_token') || hash.includes('refresh_token'));

    const clearAuthCallbackTimeout = () => {
      if (authCallbackTimeoutId != null) {
        clearTimeout(authCallbackTimeoutId);
        authCallbackTimeoutId = null;
      }
    };

    const startAuthCallbackTimeout = (message = SHOPIFY_CALLBACK_TIMEOUT_MESSAGE) => {
      clearAuthCallbackTimeout();
      authCallbackTimeoutId = setTimeout(() => {
        if (!mounted) return;
        setConnectionErrorOnce(message);
        setLoading(false);
      }, AUTH_CALLBACK_TIMEOUT_MS);
    };

    // Shared loader — fetches profile + teams, then sets ALL state in one batch
    // to avoid intermediate re-renders (React 18 batches synchronous updates
    // but NOT across await boundaries — each await flushes the batch).
    const loadUserData = async (sessionUser, options = {}) => {
      const { force = false } = options;
      if (!mounted) return;

      // Single-flight guard: avoid concurrent duplicate profile/team queries.
      if (loadInFlightRef.current && !force) return;

      // Cooldown guard: rapid auth events (tab focus, token churn, cross-tab sync)
      // can emit multiple state changes in seconds with identical user identity.
      // Skip duplicate reloads unless explicitly forced.
      const now = Date.now();
      const sameUser = lastLoadedUserIdRef.current === sessionUser.id;
      const withinCooldown = now - lastLoadedAtRef.current < 5000;
      if (sameUser && withinCooldown && !force) return;

      loadInFlightRef.current = true;
      // Fetch all data before updating any state
      try {
        const profile = await fetchUserProfile(sessionUser.id);
        if (!mounted) return;
        const { allTeams, activeTeam } = await fetchTeams(sessionUser.id);
        if (!mounted) return;
        // Single batch — React 18 batches these synchronous calls into one render
        setUser(sessionUser);
        setUserProfile(profile || null);
        setTeams(allTeams);
        setTeam(activeTeam);
        setConnectionError(null);
        lastAuthErrorRef.current = '';
        lastLoadedUserIdRef.current = sessionUser.id;
        lastLoadedAtRef.current = Date.now();
      } finally {
        loadInFlightRef.current = false;
      }
    };

    const clearUserData = () => {
      setUser(null);
      setUserProfile(null);
      setTeam(null);
      setTeams([]);
      setActiveTeamId(null);
    };

    const initAuth = async () => {
      let deferLoadingToAuthCallback = false;
      try {
        // Start a guard timer for callback flows, but still probe the current
        // session so we don't block loading if the session is already restored.
        if (hasAuthTokensInHash) {
          startAuthCallbackTimeout();
        }

        const { data: { session }, error } = await withTimeout(
          supabase?.auth?.getSession(),
          AUTH_SESSION_TIMEOUT_MS,
          AUTH_SESSION_TIMEOUT_MESSAGE
        );
        
        if (error) throw error;

        if (session?.user) {
          clearAuthCallbackTimeout();
          clearAuthTokenHash();
          // Skip validateSession() — getSession() already confirmed a valid
          // session with a non-expired JWT. Calling getUser() here added a
          // redundant network round-trip on every app load.
          if (mounted) {
            initHandled = true;
            await withTimeout(
              loadUserData(session.user),
              AUTH_WORKSPACE_LOAD_TIMEOUT_MS,
              AUTH_WORKSPACE_TIMEOUT_MESSAGE
            );
          }
        } else if (hasAuthTokensInHash) {
          // No session yet; keep waiting for callback auth state event.
          deferLoadingToAuthCallback = true;
          return;
        }
        
        if (mounted) {
          setConnectionError(null);
          lastAuthErrorRef.current = '';
        }
      } catch (error) {
        const suppressOnPublicRoute = shouldSuppressWorkspaceTimeoutOnPublicRoute(error);
        const logMethod = isTimeoutError(error) ? 'warn' : 'error';
        if (!suppressOnPublicRoute) {
          console[logMethod]('Auth initialization error:', error);
        } else if (import.meta.env?.DEV) {
          console.warn('Auth initialization timeout on public route; workspace preload deferred.', error);
        }

        if (mounted && !suppressOnPublicRoute) {
          setConnectionErrorOnce(error?.message);
        } else if (mounted) {
          setConnectionError(null);
          lastAuthErrorRef.current = '';
        }
      } finally {
        if (mounted && !deferLoadingToAuthCallback) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        clearAuthCallbackTimeout();

        try {
          if (event === 'SIGNED_OUT') {
            clearUserData();
          } else if (event === 'INITIAL_SESSION' && initHandled) {
            // initAuth already handled this session — skip to avoid double-fetch
          } else if (event === 'TOKEN_REFRESHED') {
            // Token was refreshed — JWT changed but user identity didn't.
            // Just update the user object reference (new token metadata)
            // WITHOUT re-querying user_profiles + team_members from Postgres.
            if (session?.user && mounted) {
              clearAuthTokenHash();
              setUser(session.user);
              setConnectionError(null);
              lastAuthErrorRef.current = '';
            }
          } else if (event === 'SIGNED_IN' && session?.user) {
            clearAuthTokenHash();
            await withTimeout(
              loadUserData(session.user, { force: true }),
              AUTH_WORKSPACE_LOAD_TIMEOUT_MS,
              AUTH_WORKSPACE_TIMEOUT_MESSAGE
            );
          } else if (event === 'USER_UPDATED' && session?.user) {
            clearAuthTokenHash();
            await withTimeout(
              loadUserData(session.user),
              AUTH_WORKSPACE_LOAD_TIMEOUT_MS,
              AUTH_WORKSPACE_TIMEOUT_MESSAGE
            );
          } else if (session?.user) {
            clearAuthTokenHash();
            await withTimeout(
              loadUserData(session.user),
              AUTH_WORKSPACE_LOAD_TIMEOUT_MS,
              AUTH_WORKSPACE_TIMEOUT_MESSAGE
            );
          } else {
            clearUserData();
          }
        } catch (error) {
          const suppressOnPublicRoute = shouldSuppressWorkspaceTimeoutOnPublicRoute(error);
          const logMethod = isTimeoutError(error) ? 'warn' : 'error';
          if (!suppressOnPublicRoute) {
            console[logMethod]('Auth state resolution error:', error);
          } else if (import.meta.env?.DEV) {
            console.warn('Auth state timeout on public route; workspace preload deferred.', error);
          }

          if (mounted && session?.user) {
            setUser(session.user);
          }

          if (mounted && !suppressOnPublicRoute) {
            setConnectionErrorOnce(error?.message || 'Failed to restore your session.');
          } else if (mounted) {
            setConnectionError(null);
            lastAuthErrorRef.current = '';
          }
        }

        // Always mark loading complete after auth state resolves
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      clearAuthCallbackTimeout();
      subscription?.unsubscribe();
    };
  }, [clearAuthTokenHash, setConnectionErrorOnce]);

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setConnectionError(null);
      
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Update last_login timestamp on all team_members rows for this user
      if (data?.user?.id) {
        supabase
          ?.from('team_members')
          ?.update({ last_login: new Date().toISOString() })
          ?.eq('user_id', data.user.id)
          ?.then(({ error: updateErr }) => {
            if (updateErr) console.error('Failed to update last_login:', updateErr);
          });
      }

      // onAuthStateChange will handle loading profile + teams
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;

      setUser(null);
      setUserProfile(null);
      setTeam(null);
      setTeams([]);
      setActiveTeamId(null);
      setConnectionError(null);
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    if (!user) {
      return { error: { message: 'No user logged in' } };
    }

    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (error) throw error;

      setUserProfile(data);
      return { data, error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { data: null, error };
    }
  };

  const value = useMemo(() => ({
    user,
    userProfile,
    team,
    teams,
    teamId: team?.id || null,
    teamRole: team?.role || null,
    loading,
    profileLoading,
    connectionError,
    signIn,
    signOut,
    switchTeam,
    refreshTeams,
    updateProfile,
    isAuthenticated: !!user,
    refreshConnection
  }), [user, userProfile, team, teams, loading, profileLoading, connectionError, switchTeam, refreshTeams]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
